/**
 * SettingsBilling — V2 Premium Experience (V2.0 Phase 5, Cluster A).
 *
 * Route: `/settings/billing`. Sandbox-mode billing settings for changing
 * plans, canceling, and reactivating. Talks to /api/billing/* endpoints.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import AppNav from "@/components/app/AppNav";
import { V2Button } from "@/components/v2/FormPrimitives";

function dollars(c) { return `$${(Number(c || 0) / 100).toFixed(0)}`; }

export default function SettingsBilling() {
  const { refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [m, p] = await Promise.all([
        api.get("/billing/me"),
        api.get("/plans"),
      ]);
      setMe(m.data);
      setPlans(p.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  async function upgrade(planCode) {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { plan_code: planCode });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  }

  async function cancel() {
    if (!window.confirm("Cancel your subscription at the end of the current period? You'll keep access until then.")) return;
    setBusy(true);
    try {
      await api.post("/billing/cancel");
      toast.success("Subscription will end at the period's close.");
      await reload();
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function reactivate() {
    setBusy(true);
    try {
      await api.post("/billing/reactivate");
      toast.success("Subscription reactivated.");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-v2-background text-v2-text">
        <AppNav />
        <div
          data-testid="settings-billing-loading"
          className="flex items-center justify-center py-24 text-v2-body-sm text-v2-textMuted gap-2"
        >
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  const sub = me?.subscription;
  const plan = me?.plan;
  const otherPlans = plans.filter(p => p.code !== sub?.plan_code);

  return (
    <div data-testid="settings-billing-page" className="min-h-screen bg-v2-background text-v2-text">
      <AppNav />
      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-v2-caption uppercase tracking-[0.16em] text-v2-accent">Billing</div>
            <h1
              className="text-v2-display-md mt-2"
              style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
            >
              Your subscription
            </h1>
          </div>
          <Link
            to="/dashboard"
            data-testid="settings-billing-back"
            className="text-v2-body-sm text-v2-textMuted hover:text-v2-text transition-colors"
          >
            ← Back to dashboard
          </Link>
        </div>

        {sub && plan && (
          <div
            data-testid="current-plan-card"
            className="rounded-v2-2xl bg-v2-surface border border-v2-accent/30 shadow-v2-card p-6 md:p-8 mb-8"
          >
            <div className="text-v2-caption uppercase tracking-[0.16em] text-v2-accent mb-1">
              Current plan
            </div>
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
              <h2
                className="text-v2-heading-lg text-v2-text"
                style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
              >
                {plan.name}
              </h2>
              <span className="text-v2-heading-md text-v2-textMuted">
                {dollars(plan.price_monthly_cents)}
                <span className="text-v2-body-sm text-v2-textSubtle">
                  {plan.price_monthly_cents === 0 ? "" : "/mo"}
                </span>
              </span>
            </div>
            <p className="text-v2-body-md text-v2-textMuted mb-4">{plan.description}</p>
            <div className="text-v2-body-sm text-v2-textMuted mb-5">
              Status: <span className="text-v2-text">{sub.status}</span>
              {sub.cancel_at_period_end && (
                <span className="ml-3 text-v2-warning">• Canceling at period end</span>
              )}
              {sub.is_stub && <span className="ml-3">• Sandbox mode (no live charges)</span>}
            </div>
            {sub.plan_code !== "free" && (
              sub.cancel_at_period_end ? (
                <V2Button
                  data-testid="settings-billing-reactivate"
                  disabled={busy}
                  onClick={reactivate}
                >
                  Reactivate subscription
                </V2Button>
              ) : (
                <button
                  data-testid="settings-billing-cancel"
                  disabled={busy}
                  onClick={cancel}
                  className="inline-flex items-center gap-2 rounded-v2-md border border-v2-border px-4 py-2.5 text-v2-body-sm text-v2-textMuted hover:text-v2-text hover:bg-v2-surfaceSubtle transition-colors"
                >
                  Cancel at period end
                </button>
              )
            )}
          </div>
        )}

        {otherPlans.length > 0 && (
          <>
            <h3
              className="text-v2-heading-md mb-4"
              style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
            >
              Change plan
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {otherPlans.map(p => (
                <div
                  key={p.id}
                  data-testid={`settings-billing-plan-${p.code}`}
                  className="rounded-v2-xl bg-v2-surface border border-v2-border shadow-v2-card p-5 flex flex-col"
                >
                  <div className="text-v2-caption uppercase tracking-[0.16em] text-v2-accent mb-1">{p.code}</div>
                  <h4
                    className="text-v2-heading-md text-v2-text"
                    style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
                  >
                    {p.name}
                  </h4>
                  <div className="text-v2-heading-md text-v2-text mt-1 mb-2">
                    {dollars(p.price_monthly_cents)}
                    <span className="text-v2-body-sm text-v2-textSubtle">
                      {p.price_monthly_cents === 0 ? "" : "/mo"}
                    </span>
                  </div>
                  <p className="text-v2-body-sm text-v2-textMuted mb-4 leading-relaxed flex-1">
                    {p.description}
                  </p>
                  <button
                    data-testid={`settings-billing-upgrade-${p.code}`}
                    disabled={busy}
                    onClick={() =>
                      p.code === "free"
                        ? toast.message("To downgrade, cancel current plan first.")
                        : upgrade(p.code)
                    }
                    className="inline-flex items-center gap-2 rounded-v2-md px-3.5 py-2 text-v2-body-sm font-semibold bg-v2-accent text-v2-accentInk hover:bg-v2-accentStrong shadow-v2-card hover:shadow-v2-lifted transition-all disabled:opacity-60 self-start"
                  >
                    {p.code === "free" ? "Downgrade" : "Upgrade"} <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        <p className="text-v2-body-sm text-v2-textSubtle mt-10 max-w-xl leading-relaxed">
          In sandbox mode, checkout routes you to an in-app confirmation page instead of Stripe —
          no real cards are charged. Switch to live mode in System Config to enable real payments.
        </p>
      </main>
    </div>
  );
}
